import { Capacitor } from "@capacitor/core";
import { Logger, LogLevel } from "@aparajita/capacitor-logger";

// Emit to syslog so macOS Console can see the messages (Xcode only shows stdout).
const logger = new Logger("ALLARIA_NATIVE", { level: LogLevel.debug });
logger.useSyslog = true; // setter notifies native plugin to forward to OSLog

const serializeArg = (arg: unknown): string => {
  if (arg instanceof Error) {
    const stack = arg.stack ? `\n${arg.stack}` : "";
    return `${arg.name}: ${arg.message}${stack}`;
  }

  if (typeof arg === "object") {
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }

  if (typeof arg === "undefined") return "undefined";

  return String(arg);
};

const joinArgs = (args: unknown[]): string =>
  args.map((arg) => serializeArg(arg)).join(" ");

let consoleBridgeInstalled = false;

// Redirect console.* to capacitor-logger on native so we capture them in OSLog.
export const bridgeConsoleToLogger = (): void => {
  if (consoleBridgeInstalled) return;
  if (!Capacitor.isNativePlatform()) return;

  const handlers: Record<
    "log" | "info" | "warn" | "error",
    (message: string) => void
  > = {
    log: (message) => logger.info(message),
    info: (message) => logger.info(message),
    warn: (message) => logger.warn(message),
    error: (message) => logger.error(message),
  };

  (Object.keys(handlers) as (keyof typeof handlers)[]).forEach((method) => {
    // eslint-disable-next-line no-console
    console[method] = (...args: unknown[]) => {
      const message = joinArgs(args);
      handlers[method](message);
    };
  });

  consoleBridgeInstalled = true;
};

export default logger;
